import { User } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { DashboardCard } from "./DashboardCard";
import { Building, LinkIcon, DollarSign, Users, PlusCircle, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { getQueryFn } from "@/lib/queryClient";

interface AgentDashboardProps {
  user: User;
}

// Type definitions for better type safety
interface Property {
  id: number;
  title: string;
  area: string;
  bedrooms: number;
  bathrooms: number;
  basePrice: number;
  status: string;
}

interface Booking {
  id: number;
  propertyId: number;
  status: string;
}

interface Markup {
  id: number;
  propertyId: number;
  markupPercentage: number;
}

export function AgentDashboard({ user }: AgentDashboardProps) {
  // Fetch all available properties (using public properties endpoint for active properties)
  const { data: propertiesData = [], isLoading: propertiesLoading } = useQuery<Property[]>({
    queryKey: ['public-properties'],
    queryFn: getQueryFn(),
    enabled: !!user && user.role === 'agent',
  });
  
  // Fetch bookings through agent's links
  const { data: bookingsData = [], isLoading: bookingsLoading } = useQuery<Booking[]>({
    queryKey: ['agent/bookings'],
    queryFn: getQueryFn(),
    enabled: !!user && user.role === 'agent',
  });
  
  // Fetch agent's custom links
  const { data: linksData = [], isLoading: linksLoading } = useQuery({
    queryKey: ['agent/links'],
    queryFn: getQueryFn(),
    enabled: !!user && user.role === 'agent',
  });
  
  // Fetch agent's markups
  const { data: markupsData = [], isLoading: markupsLoading } = useQuery<Markup[]>({
    queryKey: ['agent/markups'],
    queryFn: getQueryFn(),
    enabled: !!user && user.role === 'agent',
  });
  
  // Safely cast data with type assertions
  const properties = propertiesData as Property[];
  const bookings = bookingsData as Booking[];
  const markups = markupsData as Markup[];
  
  // Calculate counts and totals from real data
  const activeProperties = properties.filter((p) => p.status === 'active').length;
  const guestInquiries = bookings.filter((b) => b.status === 'pending').length;
  const totalEarnings = user.totalEarnings || 0;
  
  // Default base price (in AED) if property doesn't have one
  const DEFAULT_BASE_PRICE = 500;
  
  // Combine property data with markups for display
  const propertiesWithMarkup = properties
    .filter((p) => p.status === 'active')
    .map((property) => {
      // Find markup for this property (if exists)
      const markup = markups.find((m) => m.propertyId === property.id);
      const markupPercentage = markup ? markup.markupPercentage : (user.globalMarkupPercentage || 10);
      
      // Count bookings for this property
      const propertyBookings = bookings.filter((b) => b.propertyId === property.id);
      
      // Use basePrice from property if available, otherwise use default
      // Note: basePrice in DB is stored in cents, so divide by 100 for AED
      const priceInAED = property.basePrice ? (property.basePrice / 100) : DEFAULT_BASE_PRICE;
      
      return {
        id: property.id,
        name: property.title,
        area: property.area,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        basePrice: priceInAED, 
        yourMarkup: priceInAED * (markupPercentage / 100), // Calculate markup amount
        markupPercentage,
        totalBookings: propertyBookings.length
      };
    });

  // Functions to format data for display
  const formatCurrency = (amount: number) => {
    return `AED ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const isLoading = propertiesLoading || bookingsLoading || linksLoading || markupsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary mr-2" />
        <span className="text-muted-foreground">Loading agent dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold">Welcome, {user.fullName ? user.fullName.split(' ')[0] : user.username}</h2>
        <p className="text-muted-foreground">Here's an overview of your agent activities</p>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <DashboardCard
          title="Active Properties"
          value={activeProperties}
          icon={<Building className="h-6 w-6" />}
          subtitle="Properties you can create links for"
        />
        <DashboardCard
          title="Guest Inquiries"
          value={guestInquiries}
          icon={<Users className="h-6 w-6" />}
          subtitle="Pending inquiries from guests"
          iconBgClass="bg-blue-100"
          iconTextClass="text-blue-500"
        />
        <DashboardCard
          title="Total Earnings"
          value={formatCurrency(totalEarnings)}
          icon={<DollarSign className="h-6 w-6" />}
          subtitle="Commissions from bookings"
          iconBgClass="bg-green-100"
          iconTextClass="text-green-500"
        />
      </div>

      {/* Action button */}
      <div className="my-8">
        <Button 
          size="lg" 
          className="flex items-center gap-2"
          asChild
        >
          <Link href="/agent/links">
            <LinkIcon className="h-5 w-5" />
            <span>Create New Guest Link</span>
          </Link>
        </Button>
      </div>

      {/* Properties section */}
      <Card>
        <CardHeader>
          <CardTitle>Available Properties</CardTitle>
        </CardHeader>
        <CardContent>
          {propertiesWithMarkup.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Bedrooms</TableHead>
                  <TableHead>Base Price</TableHead>
                  <TableHead>Your Markup</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {propertiesWithMarkup.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell className="font-medium">{property.name}</TableCell>
                    <TableCell>{property.area}</TableCell>
                    <TableCell>{property.bedrooms} BD / {property.bathrooms} BA</TableCell>
                    <TableCell>{formatCurrency(property.basePrice)}</TableCell>
                    <TableCell>
                      {formatCurrency(property.yourMarkup)}
                      <div className="text-xs text-muted-foreground">({property.markupPercentage}%)</div>
                    </TableCell>
                    <TableCell>{property.totalBookings}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1"
                        asChild
                      >
                        <Link href={`/agent/links/create?propertyId=${property.id}`}>
                          <LinkIcon className="h-3.5 w-3.5" />
                          <span>Share</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No properties available. Check back later for new listings.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
